import { useEffect, useState } from "react";
import {
  Modal,
  Stack,
  Textarea,
  Select,
  Group,
  Button,
  Text,
  Alert,
} from "@mantine/core";
import { IconCoins, IconPlaylistAdd, IconInfoCircle } from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import { useLocation } from "wouter";
import { captureLastFrame } from "../Editor/captureLastFrame";
import {
  generateVideo,
  getVideoModels,
  uploadImage,
} from "../api";

// Extend an existing video into a chain. Captures the source's last frame in
// the browser, uploads it, and submits a new video generation seeded by that
// frame. The backend groups the result into a VideoChain bundle (find-or-create)
// so the chain appears as one card in the gallery.
//
// Defaults to the source's own BaseModel — the chain is "more of the same look,"
// not a model switch. User can override via the Model select.
export default function ExtendVideoModal({ opened, onClose, source }) {
  const [, setLocation] = useLocation();
  const [models, setModels] = useState([]);
  const [baseModel, setBaseModel] = useState(null);
  const [duration, setDuration] = useState("5");
  const [prompt, setPrompt] = useState("");
  const [busy, setBusy] = useState(false);

  // Reset state every time we re-open with a new source — modal can be reused.
  useEffect(() => {
    if (!opened || !source) return;
    setPrompt(source.generateConfig?.prompt ?? "");
    setBaseModel(source.generateConfig?.baseModel ?? null);
  }, [opened, source]);

  useEffect(() => {
    if (!opened) return;
    getVideoModels()
      .then((data) => {
        setModels(data);
        // Fall back to wan-2.5 if source didn't carry a BaseModel (legacy videos).
        if (!baseModel && data.length > 0) {
          const preferred =
            data.find((m) => m.baseModel === "wan-2.5") ?? data[0];
          setBaseModel(preferred.baseModel);
          setDuration(String(preferred.allowedSeconds[0]));
        }
      })
      .catch(() => {});
  }, [opened, baseModel]);

  const activeModel = models.find((m) => m.baseModel === baseModel);
  const allowedSeconds = activeModel?.allowedSeconds ?? [5, 10];
  // Snap duration into the active model's allowed set whenever the model changes.
  useEffect(() => {
    if (activeModel && !activeModel.allowedSeconds.includes(Number(duration))) {
      setDuration(String(activeModel.allowedSeconds[0]));
    }
  }, [activeModel, duration]);

  const creditPreview = activeModel
    ? activeModel.creditsPerSecond * Number(duration)
    : 0;

  const handleSubmit = async () => {
    if (!source?.videoUrl) return;
    setBusy(true);
    try {
      const blob = await captureLastFrame(source.videoUrl);
      if (!blob) {
        notifications.show({
          title: "Frame capture failed",
          message:
            "We couldn't grab the last frame of this video. If you just generated it, try again in a moment.",
          color: "red",
        });
        return;
      }

      // uploadImage takes a Blob/File; wrap with a name so the backend dedupe
      // path has something to log.
      const file = new File([blob], `extend-${source.logicalImageId}.jpg`, {
        type: "image/jpeg",
      });
      const uploaded = await uploadImage(file);
      const newImageId = Array.isArray(uploaded)
        ? uploaded[0]?.logicalImageId
        : uploaded?.logicalImageId;
      if (!newImageId) {
        notifications.show({
          title: "Upload failed",
          message: "Couldn't upload the captured frame. Please try again.",
          color: "red",
        });
        return;
      }

      const dto = {
        prompt: prompt.trim(),
        duration: Number(duration),
        baseModel,
        aspectRatio: "9:16",
        inputImageId: newImageId,
        extendSourceLogicalImageId: source.logicalImageId,
        config: {},
      };
      const job = await generateVideo(dto);
      onClose();
      if (job.bundleId) setLocation(`/bundle/${job.bundleId}`);
    } catch (err) {
      notifications.show({
        title: "Extend failed",
        message: err.message ?? "Unknown error",
        color: "red",
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Extend this clip"
      size="md"
      centered
    >
      <Stack gap="sm">
        <Alert
          color="blue"
          variant="light"
          p="xs"
          icon={<IconInfoCircle size={16} />}
        >
          The new clip starts from the last frame of this one. Expect some
          color drift and motion slowdown — quality is best at 2–3 chain steps.
        </Alert>

        <Textarea
          label="Prompt"
          required
          autosize
          minRows={3}
          maxRows={6}
          value={prompt}
          onChange={(e) => setPrompt(e.currentTarget.value)}
        />

        {models.length > 0 && (
          <Select
            label="Model"
            value={baseModel}
            onChange={setBaseModel}
            data={models.map((m) => ({
              value: m.baseModel,
              label: m.displayLabel,
            }))}
            allowDeselect={false}
          />
        )}

        <Select
          label="Length"
          value={duration}
          onChange={setDuration}
          data={allowedSeconds.map((s) => ({
            value: String(s),
            label: `${s}s`,
          }))}
          allowDeselect={false}
        />

        <Group justify="space-between" mt="sm">
          <Text size="xs" c="dimmed">
            Frame capture happens in your browser; no upload until you submit.
          </Text>
          <Button
            onClick={handleSubmit}
            loading={busy}
            disabled={!prompt.trim() || !baseModel}
            leftSection={<IconPlaylistAdd size={16} stroke={1.5} />}
            rightSection={
              <Group gap={4} wrap="nowrap">
                <IconCoins stroke={1.5} size={13} /> {creditPreview}
              </Group>
            }
          >
            Extend clip
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
